// Supabase client + a global axios interceptor that attaches the signed-in user's access token to
// every backend call. The backend verifies that token and derives the user id from it, so the app
// can no longer act as an arbitrary user by setting a localStorage int.
import { createClient } from "@supabase/supabase-js";
import axios from "axios";

const url = process.env.REACT_APP_SUPABASE_URL;
const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!url || !anon) {
  // Loud in dev, harmless in prod builds where the vars are set — better than a silent blank screen.
  console.error("Supabase env missing: set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.");
}

export const supabase = createClient(url || "http://localhost", anon || "public-anon-key", {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

// Attach the bearer token to outgoing API requests. getSession() is cheap (reads local storage) and
// refreshes transparently when near expiry.
axios.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export async function signInWithGoogle() {
  // Without env vars the client above is a localhost dummy: OAuth "succeeds", no session comes back,
  // and the user silently bounces to Login. Fail visibly instead of leaving them guessing.
  if (!url || !anon) {
    return { error: { message: "Sign-in is misconfigured — Supabase env vars are missing on this deployment." } };
  }
  // redirectTo must be in Supabase → Auth → URL Configuration → Redirect URLs, or Supabase falls back
  // to the Site URL and the tokens never reach this origin. detectSessionInUrl (above) does the rest.
  // Return to /login, not the Landing page: /login is the gate that forwards a fresh session into the
  // app (→ /dashboard → OnboardGate pushes new users to onboarding). Landing has no such forward, so
  // returning to origin left new sign-ups stranded on the marketing page.
  return supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${window.location.origin}/login` },
  });
}

// Email + password, the same Supabase session Google produces — so the axios interceptor, the
// backend token check and OnboardGate all work unchanged. `mode` picks sign-in vs sign-up.
export async function signInWithEmail(email, password, mode = "signin") {
  if (!url || !anon) {
    return { error: { message: "Sign-in is misconfigured — Supabase env vars are missing on this deployment." } };
  }
  if (mode === "signup") {
    return supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
  }
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  await supabase.auth.signOut();
}
